import type { Client, ClientContact, ClientRow } from '@/shared/api'
import { formatContactValue, formatPhone } from './phone'

/**
 * Чем назвать гостя в шапке.
 *
 * ⚠️ ИМЯ МОЖЕТ БЫТЬ ПУСТЫМ, И ЭТО ШТАТНО, А НЕ СБОЙ ЗАГРУЗКИ (§3): гость, о котором известен только
 * телефон, — норма, в реальной выгрузке таких было 118 из 14 610. Поэтому пустое имя не «чинится»
 * прочерком и не прячет карточку: заголовком становится то, чем гость реально опознаётся, а рядом
 * прямо говорится, что имени нет, — и это приглашение его добавить, а не сообщение об ошибке.
 */
export type ClientTitle = {
  /** Что показать крупно: имя, а если его нет — телефон или другой известный канал. */
  title: string
  /** Почему заголовок такой. Есть только у безымянного. */
  namelessHint?: string
  /**
   * Заголовок — это НОМЕР или id, а не слова: его стоит набирать моноширинным, цифры так читаются
   * разряд за разрядом. У запасной фразы «Гость без имени» признак снят — она не число.
   */
  mono?: boolean
}

/**
 * Канал, которым гость опознаётся в первую очередь: основной телефон, иначе любой телефон, иначе
 * первый известный канал (у пришедшего из бота это может быть telegram-id — и это всё, что о нём
 * известно).
 */
export function clientPrimaryContact(client: Client): ClientContact | undefined {
  return (
    client.contacts.find((contact) => contact.kind === 'PHONE_PRIMARY') ??
    client.contacts.find((contact) => /^\+\d+$/.test(contact.value)) ??
    client.contacts[0]
  )
}

export function clientTitle(client: Client): ClientTitle {
  // `.trim()`, а не сравнение с пустой строкой: имена приезжают из грязных выгрузок, и строка из
  // одних пробелов — это то же «имени нет», только выглядящее как пустой заголовок.
  if (client.displayName.trim() !== '') return { title: client.displayName }

  const contact = clientPrimaryContact(client)
  if (contact) {
    return {
      title: formatContactValue(contact.value),
      namelessHint: 'имя не указано',
      mono: true,
    }
  }

  // Ни имени, ни единого канала. Так выглядит запись, оставшаяся от снятого канала: показать
  // нечего, но и молчать нельзя — иначе шапка карточки просто пустая.
  return { title: 'Гость без имени', namelessHint: 'ни имени, ни каналов связи' }
}

/**
 * То же правило для СТРОКИ СПИСКА. Отдельная функция, а не общая с карточкой, потому что и данные
 * другие: в строке есть только основной телефон (`primaryPhone`), а не весь список каналов, — и он
 * тоже бывает пустым, если гость пришёл из бота и известен по telegram-id.
 */
export function clientRowTitle(row: ClientRow): ClientTitle {
  if (row.displayName.trim() !== '') return { title: row.displayName }
  if (row.primaryPhone !== '') {
    return { title: formatPhone(row.primaryPhone), namelessHint: 'имя не указано', mono: true }
  }
  return { title: 'Гость без имени', namelessHint: 'ни имени, ни телефона' }
}
